import { ColumnDef } from "@tanstack/react-table";
import { DataTableColumnHeader } from "@repo/ui";
import type { Dispense } from "@/lib/types/inventory";
import { NdcLink } from "@/components/ndc-link";
import { DateTimeCell } from "@/components/date-time-cell";
import { NotesCell } from "@/components/notes-cell";
import { TransactionRowActions } from "@/components/transaction-row-actions";

export function createDispenseColumns(
  onView: (dispense: Dispense) => void,
  onEdit?: (dispense: Dispense) => void,
  onDelete?: (dispense: Dispense) => void,
  onEditNotes?: (dispense: Dispense) => void,
  onNDCClick?: (ndc: string) => void,
  onAddToReminder?: (dispense: Dispense) => void
): ColumnDef<Dispense>[] {
  return [
    {
      accessorKey: "date",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Date & Time" />
      ),
      cell: ({ row }) => {
        const dateValue = row.getValue("date") as Date | string;
        if (!dateValue) return <span className="text-muted-foreground">—</span>;
        const date = new Date(dateValue + "T00:00:00");
        const dispensedAt = row.original.dispensed_at
          ? new Date(row.original.dispensed_at)
          : null;
        return <DateTimeCell date={date} time={dispensedAt} />;
      },
      enableSorting: true,
    },
    {
      accessorKey: "item_name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Product" />
      ),
      cell: ({ row }) => (
        <span className="font-medium">{row.getValue("item_name")}</span>
      ),
    },
    {
      accessorKey: "ndc",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="NDC" />
      ),
      cell: ({ row }) => (
        <NdcLink ndc={row.original.ndc} onClick={onNDCClick} />
      ),
    },
    {
      accessorKey: "quantity_dispensed",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Quantity"
          className="justify-center"
        />
      ),
      cell: ({ row }) => (
        <div className="text-center font-semibold">
          {row.getValue("quantity_dispensed")}
        </div>
      ),
    },
    {
      accessorKey: "rx_number",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="RX Number" />
      ),
      cell: ({ row }) => (
        <span className="font-mono text-sm">{row.getValue("rx_number")}</span>
      ),
    },
    {
      accessorKey: "patient_name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Patient" />
      ),
      cell: ({ row }) => {
        const patientName = row.getValue("patient_name") as string | null;
        return patientName ? (
          <span className="text-sm">{patientName}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
    },
    {
      id: "notes",
      header: () => <div className="text-center">Notes</div>,
      cell: ({ row }) => (
        <NotesCell
          hasNotes={(row.original.notes_count ?? 0) > 0}
          onClickNotes={() => onEditNotes?.(row.original)}
        />
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <TransactionRowActions
          record={row.original}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          onAddToReminder={onAddToReminder}
        />
      ),
    },
  ];
}
